import { useLoadingToast } from '@gettactic/helpers/src/hooks/toasts/useLoadingToast';
import { useTacticHooks } from '@gettactic/hooks';
import { useMapCanvas } from '../../contexts/MapCanvasContext';
import {
  AddImageFromFileResult,
  AddSvgFromFileResult,
  useMapEditorActions
} from '../../contexts/MapEditorContext';

export interface UploadFloorPlanOptions {
  imageType?: 'svg' | 'png' | 'jpg';
  type?: 'background' | 'floor';
  resizeEditingZone?: boolean;
  replaceAll?: boolean;
}

export const useUploadFloorPlan = () => {
  const { addSvgFromFile, addImageFromFile, deleteShape } =
    useMapEditorActions();
  const { updateEditingZoneSize, shapesRef } = useMapCanvas();
  const { api } = useTacticHooks();

  const toast = useLoadingToast({
    loadingMessage: 'Uploading floor plan...'
  });

  return async (file: File | undefined, options?: UploadFloorPlanOptions) => {
    if (!file) return;

    const {
      imageType = 'svg',
      type = 'floor',
      resizeEditingZone = true,
      replaceAll = true
    } = options || {};

    toast.startLoading();

    const getUploadUrlResult = await api.client.organizations.getUploadUrl(
      {
        type: 'map:background',
        format: imageType
      },
      file
    );

    const uploadUrl = getUploadUrlResult?.result?.upload_url;
    const fileUrl = getUploadUrlResult?.result?.url;

    try {
      const reader = new FileReader();

      reader.onload = async (event) => {
        const fileContent = event.target?.result;
        const mimeType =
          imageType === 'svg'
            ? 'image/svg+xml'
            : imageType === 'png'
              ? 'image/png'
              : 'image/jpeg';

        const blob = new Blob([fileContent as ArrayBuffer | string], {
          type: mimeType
        });
        const url = URL.createObjectURL(blob);

        if (replaceAll) {
          // Clear existing shapes
          const shapesToDelete = shapesRef.current.filter(
            (shape) => shape.layerType === type
          );
          shapesToDelete.forEach((shape) => deleteShape(shape.id));
        }

        if (uploadUrl && fileUrl) {
          const uploadResult = await fetch(uploadUrl, {
            method: 'PUT',
            body: fileContent as BodyInit
          });

          if (uploadResult.status !== 200) {
            throw new Error('Failed to upload file');
          }
        } else {
          throw new Error('Failed to get upload URL');
        }

        let result: AddSvgFromFileResult | AddImageFromFileResult | undefined =
          undefined;

        if (imageType === 'svg') {
          result = await addSvgFromFile(fileUrl, {
            shape: {
              x: 0,
              y: 0,
              layerType: type,
              locked: true
            },
            saveContent: false,
            saveOriginalUrl: true
          });
        } else {
          result = await addImageFromFile(fileUrl, {
            shape: {
              x: 0,
              y: 0,
              layerType: type,
              locked: true
            },
            saveOriginalUrl: true
          });
        }

        if (resizeEditingZone) {
          if (result && 'svg' in result) {
            if (result?.svg?.width && result?.svg?.height) {
              updateEditingZoneSize(result.svg.width, result.svg.height);
            } else if (
              result &&
              'width' in result &&
              'height' in result &&
              result.width &&
              result.height
            ) {
              updateEditingZoneSize(
                Number(result.width),
                Number(result.height)
              );
            }
          }
        }

        URL.revokeObjectURL(url);

        toast.success('Floor plan uploaded');
      };

      // Read file differently based on type
      if (imageType === 'svg') {
        reader.readAsText(file);
      } else {
        reader.readAsArrayBuffer(file);
      }
    } catch (error) {
      toast.dismiss();
      throw new Error(`Error loading ${imageType.toUpperCase()} file`);
    }
  };
};
